import React, { useContext, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import AuthContainer from "../../containers/Auth";
import { useNavigate } from "react-router-dom";
import { TokenContext } from "../../service/context/tokencontent";
import { validationSchema } from "../../service/validation/otpVerification";
import CustomModal from "../../components/Modal";
import OtpInput from "../../components/OTP";
import { ApiService } from "../../service/api.service";
import { check, verificationbadge } from "../../common/svg";
import SecureImage from "../../components/BucketImage/Image";
import {PhoneAuthProvider,RecaptchaVerifier,signInWithCredential,signInWithPhoneNumber,} from "firebase/auth";
import { auth } from "../../service/firebase/firebase";
import toast from "react-hot-toast";

const MobileOTPVerification = () => {
  const apiService = new ApiService();
  const tokenContext = useContext(TokenContext);
  const navigate = useNavigate();

  // Form setup
  const {
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
  } = useForm({
    defaultValues: {
      mobile: "",
    },
    mode: "onChange",
    resolver: yupResolver(validationSchema),
  });

  const [isModalOpen, setModalOpen] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [mobileTimer, setMobileTimer] = useState<number>(30);
  const [mobileVerified, setMobileVerified] = useState<boolean>(false);
  const [error, setError] = useState<string>("");
  const [resetMobileOtp, setResetMobileOtp] = useState<boolean>(false);
  const entityId: string | null = localStorage.getItem("entity_id");
  const verificationId: string | null = localStorage.getItem(
    "motsop-verficationId"
  );
  const mobile: string = watch("mobile");

  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (mobileTimer > 0) {
      timer = setTimeout(() => setMobileTimer(mobileTimer - 1), 1000);
    }
    return () => clearTimeout(timer);
  }, [mobileTimer]);
  const closeModal = (): void => {
    setModalOpen(false);
    navigate("/confirmpassword");
  };

  useEffect(() => {
    let timer: NodeJS.Timeout | undefined;
    if (isModalOpen) {
      timer = setTimeout(() => {
        closeModal(); // auto-close modal after 5s
      }, 5000);
    }
    return () => clearTimeout(timer); // cleanup if modal closes before 5s
  }, [isModalOpen, closeModal]);

  useEffect(() => {
    if (mobileVerified) {
      setModalOpen(true);
    }
  }, [mobileVerified]);

  const resendMobileOTP = async (): Promise<void> => {
    const phoneNumber = localStorage.getItem("phoneNumber");

    if (!phoneNumber) {
      console.error("Phone number is missing");
      toast.error(
        "Phone number is missing. Please go back and enter your phone number."
      );
      return;
    }

    try {
      if (window.recaptchaVerifier) {
        window.recaptchaVerifier.clear();
      }

      window.recaptchaVerifier = new RecaptchaVerifier(
        auth,
        "recaptcha-container",
        {
          size: "invisible",
          callback: (response: any) => {
            console.log("reCAPTCHA resolved");
          },
          "expired-callback": () => {
            console.warn("reCAPTCHA expired. Please retry.");
          },
        }
      );

      await window.recaptchaVerifier.render();

      const confirmationResult = await signInWithPhoneNumber(
        auth,
        `+91${phoneNumber}`,
        window.recaptchaVerifier
      );

      localStorage.setItem(
        "motsop-verficationId",
        confirmationResult.verificationId
      );

      setMobileTimer(30);
      toast.success("OTP sent successfully!");
      setValue("mobile", "");
      setResetMobileOtp((prev) => !prev);
    } catch (error: any) {
      console.error("Error sending mobile OTP:", error.message);
      toast.error(error.message || "Failed to send OTP. Please try again.");

      if (window.recaptchaVerifier) {
        window.recaptchaVerifier.clear();
        window.recaptchaVerifier = null;
      }
    }
  };

  const verifyMobileOTP = async (otp: string): Promise<void> => {
    try {
      setIsSubmitting(true);

      const currentVerificationId = localStorage.getItem(
        "motsop-verficationId"
      );

      if (!currentVerificationId || !otp) {
        setError("Verification ID or OTP is missing");
        toast.error(
          "Verification ID or OTP is missing. Please request a new OTP."
        );
        return;
      }

      try {
        const phoneCredential = PhoneAuthProvider.credential(
          currentVerificationId,
          otp
        );
        await signInWithCredential(auth, phoneCredential);
        setMobileVerified(true);
        setError("");
      } catch (credentialError: any) {
        // Firebase auth error handling
        if (credentialError.code === "auth/invalid-verification-code") {
          toast.error("Invalid OTP. Please enter the correct code.");
        } else if (credentialError.code === "auth/code-expired") {
          toast.error("OTP has expired. Please request a new code.");
        } else {
          toast.error(
            `Verification failed: ${credentialError.message || "Unknown error"}`
          );
        }
        console.error("Credential Error:", credentialError);
      }
    } catch (error: any) {
      console.error("Mobile OTP Verification Error:", error);
      toast.error(error.message || "Verification failed. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle OTP input completion
  const handleMobileOtpComplete = (otp: string): void => {
    setValue("mobile", otp);
    verifyMobileOTP(otp);
  };

  return (
    <AuthContainer>
      <div className="flex w-full rounded-[21.25px] h-full bg-white font-poppins">
        <div className="w-full h-full">
          <div className="md:mx-[4rem] my-[6rem]">
            <div className="h-[100px] w-[239px] bg-[#D9D9D9] flex items-center justify-center">
              logo
            </div>
            <br />
            <p className="font-[700] text-[20px] font-poppins leading-[41.33px] track-[1%]">
              Welcome aboard 👋
            </p>
            <p className="font-[400] font-poppins text-[16px] leading-[36.73px] track-[1%] text-[#313957]">
              Let's Get You Started
            </p>
            <br />

            <div className="space-y-4">
              <div className="flex items-center justify-center mb-2">
                <p className="font-[600] text-[18px] font-poppins leading-[38px] track-[1%]">
                  {mobileVerified
                    ? `Mobile Number Verified`
                    : `Verify your Mobile Number`}
                </p>
              </div>

              <OtpInput
                length={6}
                onComplete={handleMobileOtpComplete}
                errors={errors}
                disabled={isSubmitting || mobileVerified}
                className="custom-otp-class"
                key={resetMobileOtp ? "mobile-reset" : "mobile"}
                value=""
              />

              {error && (
                <p className="text-center text-red-500 text-[14px]">{error}</p>
              )}

              <div id="recaptcha-container" className="invisible"></div>

              <div className="mx-[20px]">
                <button
                  type="button"
                  onClick={resendMobileOTP}
                  disabled={mobileTimer > 0 || mobileVerified}
                  className={`
                    mt-4 w-full h-[50px] px-4 py-2 
                    rounded-2xl text-white text-[16px] font-medium 
                    bg-[#444FBD] 
                    transition duration-300 
                    ${
                      mobileTimer > 0 || mobileVerified
                        ? "opacity-50 cursor-not-allowed"
                        : "hover:bg-[#3a45a0] focus:ring-2 focus:ring-[#3a45a0]"
                    }
                  `}
                >
                  {mobileVerified ? (
                    "Mobile Verified Successfully"
                  ) : mobileTimer > 0 ? (
                    <>
                      <span className="mr-2">Resend Mobile OTP in {mobileTimer}s</span>
                      <svg
                        className="inline w-4 h-4 animate-spin text-white"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                        ></path>
                      </svg>
                    </>
                  ) : (
                    "Resend Mobile OTP"
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <CustomModal
        isOpen={isModalOpen}
        content={
          <div className="flex flex-col items-center p-6">
            <SecureImage
              fileKey={check}
              alt="check"
              className="mb-4 w-16 h-16"
            />
            <h3 className="text-xl font-semibold text-[#4B4B4B] mb-2">
              Mobile Verification Successful
            </h3>
            <p className="text-base text-[#4B4B4B] mb-6 text-center">
              Your mobile number has been verified successfully
            </p>
            <button
              onClick={closeModal}
              className="bg-[#444FBD] rounded-md py-2 px-6 text-white font-medium hover:bg-[#3a45a0] transition duration-300"
            >
              Continue
            </button>
          </div>
        }
        onClose={closeModal}
      />
    </AuthContainer>
  );
};

export default MobileOTPVerification;
